import React, { createContext, useContext, useState, useEffect } from 'react';
import { authAPI, childrenAPI, measurementsAPI } from '../services/api';

const AuthContext = createContext();

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children: childrenProp }) => {
  const [user, setUser] = useState(null);
  const [children, setChildren] = useState([]);
  const [measurements, setMeasurements] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Check for stored session
    const storedUser = localStorage.getItem('user');
    const token = localStorage.getItem('token');
    if (storedUser && token) {
      const userData = JSON.parse(storedUser);
      setUser(userData);
      loadUserData();
    }
    setLoading(false);
  }, []);

  const loadUserData = async () => {
    try {
      const userChildren = await childrenAPI.getAll();
      setChildren(Array.isArray(userChildren) ? userChildren : []);
      
      // Load measurements for all children
      const allMeasurements = [];
      if (Array.isArray(userChildren)) {
        for (const child of userChildren) {
          try {
            const childMeasurements = await measurementsAPI.getByChildId(child.id);
            if (Array.isArray(childMeasurements)) {
              allMeasurements.push(...childMeasurements);
            }
          } catch (error) {
            console.error(`Error loading measurements for child ${child.id}:`, error);
          }
        }
      }
      setMeasurements(allMeasurements);
    } catch (error) {
      console.error('Error loading user data:', error);
      setChildren([]);
      setMeasurements([]);
    }
  };

  const login = async (username, password) => {
    try {
      const response = await authAPI.login({ username, password });
      setUser(response.user);
      await loadUserData();
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Invalid username or password' 
      };
    }
  };

  const register = async (username, password, email, name) => {
    try {
      const response = await authAPI.register({ username, password, email, name });
      setUser(response.user);
      await loadUserData();
      return { success: true };
    } catch (error) {
      return { 
        success: false, 
        error: error.response?.data?.detail || 'Registration failed' 
      };
    }
  };

  const logout = () => {
    authAPI.logout();
    setUser(null);
    setChildren([]);
    setMeasurements([]);
  };

  const addChild = async (childData) => {
    try {
      const newChild = await childrenAPI.create(childData);
      setChildren([...children, newChild]);
      return newChild;
    } catch (error) {
      console.error('Error adding child:', error);
      throw error;
    }
  };

  const updateChild = async (childId, childData) => {
    try {
      const updatedChild = await childrenAPI.update(childId, childData);
      setChildren(children.map(child => 
        child.id === childId ? updatedChild : child
      ));
    } catch (error) {
      console.error('Error updating child:', error);
      throw error;
    }
  };

  const deleteChild = async (childId) => {
    try {
      await childrenAPI.delete(childId);
      setChildren(children.filter(child => child.id !== childId));
      setMeasurements(measurements.filter(m => m.childId !== childId));
    } catch (error) {
      console.error('Error deleting child:', error);
      throw error;
    }
  };

  const addMeasurement = async (measurementData) => {
    try {
      const newMeasurement = await measurementsAPI.create(measurementData);
      setMeasurements([...measurements, newMeasurement]);
      return newMeasurement;
    } catch (error) {
      console.error('Error adding measurement:', error);
      throw error;
    }
  };

  const getChildMeasurements = (childId) => {
    return measurements
      .filter(m => m.childId === childId || m.child_id === childId)
      .sort((a, b) => new Date(b.date) - new Date(a.date));
  };

  const value = {
    user,
    children,
    measurements,
    loading,
    login,
    register,
    logout,
    addChild,
    updateChild,
    deleteChild,
    addMeasurement,
    getChildMeasurements
  };

  return <AuthContext.Provider value={value}>{childrenProp}</AuthContext.Provider>;
};